import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { SelectAutofillCombobox } from "components/SelectAutofillCombobox";
import { Form, useFormikContext } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useParams } from "react-router-dom";
import {
  components as ReactSelectComponents,
  MultiValue,
  OptionProps,
  SingleValue,
} from "react-select";
import { useGetAutofillQuery } from "store/apis/autofill";
import {
  useCreateEntityMutation,
  useGetEntitiesQuery,
  useGetGroupDataQuery,
} from "store/apis/group";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import { BackendError } from "types/utils/error";
import AddEntity from "../addMoreEntity";
import { COMPANY_ENTITY, PERSONA } from "./AddShareholder";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { DirectorAutofill } from "types/Models/directorAutofill";
import { BankAutofill } from "types/Models/bankAutofill";
import { AddressAutofill } from "types/Models/addressAutofill";
import { NumericInput } from "components/NumericInput/NumericInput";

export type ShareholderPayloadType = {
  shareholderType: string;
  persona: string;
  entity: string;
  tin: string;
  ownership: string;
  country_id: string;
  address: string;
  issuedCommonStock: string;
  issuedPreferredStock: string;
  relationshipToEntity: string;
};

type RelationshipOptionData = OptionData & { description: string };

const RELATIONSHIP_TO_ENTITY_OPTIONS: RelationshipOptionData[] = [
  {
    value: "PARENT_HOLDING_COMPANY",
    label: "Parent/Holding company",
    description:
      "An entity whose primary purpose is to hold ownership stakes in one or more other companies, including this one. Typically part of a multi-entity corporate group.",
  },
  {
    value: "SHAREHOLDER_ENTITY",
    label: "Shareholder entity",
    description:
      "An entity that owns shares in this company but was not created primarily to hold that ownership, for example, an operating business, an individual's personal investment vehicle, or an entity required by local regulation to hold shares on behalf of an individual.",
  },
];

const RelationshipOption = (props: OptionProps<OptionData>) => (
  <ReactSelectComponents.Option
    {...props}
    className="t-cursor-pointer [&.select\_\_option--is-focused]:t-bg-surface-lighter-grey [&.select\_\_option--is-selected]:t-bg-purple-0"
  >
    <div className="t-flex t-flex-col t-gap-1 t-px-3 t-py-2.5">
      <div className="t-text-subtext-sm t-text-text-100">
        {props.data.label}
      </div>
      <div className="t-text-body-sm t-text-text-30">
        {(props.data as RelationshipOptionData).description}
      </div>
    </div>
  </ReactSelectComponents.Option>
);

export const AddShareholderForm = ({
  entityId: entityIdFromProps,
  lockedShareholderType,
}: {
  entityId?: string;
  lockedShareholderType?: string;
}) => {
  const { setFieldValue, values, errors, touched, validateOnChange } =
    useFormikContext<ShareholderPayloadType>();
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const { entityId } = useParams<{ entityId: string }>();
  const entity = entityId || entityIdFromProps;
  const { data: currentEntityDetails } = useGetEntityDataAttributesQuery(
    { groupId, entityId: entity! },
    { skip: !groupId || !entity }
  );
  const currentEntityName =
    (currentEntityDetails?.find(({ name }) => name === "entity_name")
      ?.value as string) || "this entity";
  const { isCustomer } = useRoleBasedView();
  const addEntityModal = useModal();
  const { data: countries } = useGetCountriesDataQuery();
  const [createEntity, { isLoading: isCreating }] = useCreateEntityMutation();
  const { data: autofills = [] } = useGetAutofillQuery(
    {
      groupId,
      autofillKey: "authorized_persons",
      entityId: entity!,
    },
    { skip: !groupId || !entity, refetchOnMountOrArgChange: true }
  );
  const {
    data: { groups: [group] = [null] } = {},
    refetch: refetchUserEntities,
  } = useGetEntitiesQuery(
    { include_shareholder: true },
    {
      skip: !isCustomer,
    }
  );
  let { data, refetch: refetchAdminEntities } = useGetGroupDataQuery(
    { groupId, include_shareholder: true },
    { skip: isCustomer }
  );
  const entities = (isCustomer ? group?.entities : data?.entities) || [];

  const refetchAllEntities = async () => {
    try {
      if (isCustomer) await refetchUserEntities().unwrap();
      if (!isCustomer) await refetchAdminEntities().unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const addEntityData = async (groupData: {
    company_name: any;
    formation_type_id: any;
    country_id: any;
    state_id: any;
    entity_type: any;
  }) => {
    try {
      let payload = {
        company_name: groupData.company_name,
        formation_type_id: groupData.formation_type_id,
        country_id: groupData.country_id,
        state_id: groupData.state_id,
        entity_type: groupData.entity_type,
      };
      const entity = await createEntity({ groupId, payload }).unwrap();
      addEntityModal.close();

      await refetchAllEntities();
      setFieldValue("entity", entity.company_id || "");
      setFieldValue("country_id", entity.country_id || "");
      successToast({ message: "Entity created successfully" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handlePersonaChange = (personaUuid: string) => {
    const selectedPersona = autofills.find(({ uuid }) => uuid === personaUuid);

    if (selectedPersona) {
      setFieldValue("persona", selectedPersona.uuid || "");
      setFieldValue("tin", selectedPersona.tax_id || "");
      setFieldValue("ownership", selectedPersona.ownership_stake || "");
      setFieldValue("address", selectedPersona.address_id || "");
    }
  };

  const handlePersonaSelect = (
    selectedPersona:
      | PersonaAutofill
      | BankAutofill
      | AddressAutofill
      | DirectorAutofill
  ) => {
    if (selectedPersona) {
      setFieldValue("persona", selectedPersona.uuid || "");
      setFieldValue("tin", (selectedPersona as PersonaAutofill).tax_id || "");
      setFieldValue(
        "ownership",
        (selectedPersona as PersonaAutofill).ownership_stake || ""
      );
      setFieldValue(
        "address",
        (selectedPersona as PersonaAutofill).address_id || ""
      );
    }
  };

  const handleEntitySelect = (
    selectedOption: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    const selectedEntity = entities.find(
      ({ uuid }) => uuid === (selectedOption as SingleValue<OptionData>)?.value
    );

    if (selectedEntity) {
      setFieldValue("entity", selectedEntity.uuid || "");
      setFieldValue("tin", selectedEntity.ein_number || "");
      setFieldValue("country_id", selectedEntity.country_id || "");
    }
  };

  const selectedCountry = countries?.find(
    ({ uuid }) => uuid === values.country_id
  );

  const selectedEntity = entities.find(({ uuid }) => uuid === values.entity);

  const shareholderTypeOptions = [
    { label: "Individual", value: PERSONA },
    { label: "Entity", value: COMPANY_ENTITY },
  ];

  const selectedShareholderType = values.shareholderType
    ? shareholderTypeOptions.find(
        ({ value }) => value === values.shareholderType
      ) || null
    : null;

  return (
    <Form className="t-m-0 t-space-y-3">
      {!lockedShareholderType && (
        <Combobox
          required
          label="Shareholder type"
          placeholder="Select"
          withForm
          options={shareholderTypeOptions}
          name="shareholderType"
          menuPortalTarget={document.body}
          value={selectedShareholderType}
        />
      )}
      {values.shareholderType === PERSONA && (
        <SelectAutofillCombobox
          placeholder="Select"
          entityId={entity!}
          type="authorized_persons"
          withForm
          name="persona"
          selected={values?.persona}
          label="Persona"
          required
          onChange={handlePersonaChange}
          onSelect={handlePersonaSelect}
          menuPortal={null}
        />
      )}

      {values.shareholderType === COMPANY_ENTITY && (
        <Combobox
          label="Entity"
          placeholder="Select"
          withForm
          options={entities?.map((entity) => ({
            value: entity.uuid,
            label: entity.name,
          }))}
          name="entity"
          required
          actions={
            <Button
              type="button"
              customType="link"
              onClick={addEntityModal.open}
            >
              Add new entity
            </Button>
          }
          onChange={handleEntitySelect}
          value={
            selectedEntity && {
              value: selectedEntity.uuid,
              label: selectedEntity.name,
            }
          }
        />
      )}

      {values.shareholderType === COMPANY_ENTITY && (
        <Combobox
          label={`Relationship to ${currentEntityName}`}
          placeholder="Select"
          withForm
          options={RELATIONSHIP_TO_ENTITY_OPTIONS}
          components={{ Option: RelationshipOption }}
          name="relationshipToEntity"
          required
          menuPortalTarget={document.body}
          value={
            RELATIONSHIP_TO_ENTITY_OPTIONS.find(
              ({ value }) => value === values.relationshipToEntity
            ) || null
          }
        />
      )}

      {values.shareholderType !== "" && (
        <>
          {values.shareholderType === PERSONA && (
            <div>
              <TextInput
                name="tin"
                label="TIN"
                placeholder="Enter"
                showErrorOnceTouched
              />
              <span className="t-mt-1.5 t-text-body-sm t-text-text-30 t-leading-none">
                Required if shareholder is a US citizen. (This will be stored
                securely in the dashboard)
              </span>
            </div>
          )}
          <NumericInput
            fieldProps={{ name: "ownership" }}
            storeNumeric
            numericProps={{
              fixedDecimalScale: true,
              decimalScale: 2,
              suffix: " %",
              allowNegative: false,
              customType:
                errors.ownership && (validateOnChange || touched.ownership)
                  ? "error"
                  : undefined,
            }}
            label="Ownership"
            required
          />
          {errors.ownership && (validateOnChange || touched.ownership) && (
            <span className="t-text-red-50 t-text-body-sm t-italic">
              {errors.ownership}
            </span>
          )}
          <div className="t-flex t-gap-3 sm:t-flex-col">
            <NumericInput
              fieldProps={{ name: "issuedCommonStock" }}
              storeNumeric
              numericProps={{
                decimalScale: 0,
                allowNegative: false,
              }}
              label="Issued common stock"
            />
            <NumericInput
              fieldProps={{ name: "issuedPreferredStock" }}
              storeNumeric
              numericProps={{
                decimalScale: 0,
                allowNegative: false,
              }}
              label="Issued preferred stock"
            />
          </div>
          <Combobox
            label="Tax Residency"
            placeholder="Select"
            withForm
            options={countries?.map((country) => ({
              value: country.uuid,
              label: country.name,
            }))}
            name="country_id"
            required
            menuPortalTarget={document.body}
            value={
              selectedCountry && {
                label: selectedCountry.name,
                value: selectedCountry.uuid,
              }
            }
          />
          <SelectAutofillCombobox
            withForm
            type="addresses"
            selected={values.address}
            name="address"
            entityId={entity!}
            label={
              values.shareholderType === COMPANY_ENTITY
                ? "Legal Address"
                : "Address"
            }
            required={values.shareholderType === COMPANY_ENTITY}
            onChange={(addressId) => setFieldValue("address", addressId)}
            srollToSave
            size="regular"
            error={
              errors.address && touched.address ? errors.address : undefined
            }
          />
        </>
      )}
      <AddEntity
        addEntityData={addEntityData}
        showAddEntity={addEntityModal.isOpen}
        setShowAddEntity={addEntityModal.close}
        showEntityTypeOption
        isCreating={isCreating}
      />
    </Form>
  );
};
